import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import API from '../services/api';

function OrganizationsList() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadOrganizations(); }, []);

  const loadOrganizations = async () => {
    try {
      const response = await API.get('/admin/organizations');
      setOrganizations(response.data.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOrganization = async (id, currentStatus, name) => {
    if (!window.confirm(`${currentStatus ? 'Deactivate' : 'Activate'} "${name}"?`)) return;
    const toastId = toast.loading(`${currentStatus ? 'Deactivating' : 'Activating'} client...`);
    try {
      await API.patch(`/admin/organization/${id}/toggle`, { is_active: !currentStatus });
      toast.success(`Client ${currentStatus ? 'deactivated' : 'activated'}!`, { id: toastId });
      loadOrganizations();
    } catch (error) {
      toast.error('Failed to toggle client', { id: toastId });
    }
  };

  const handleDeleteOrganization = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const toastId = toast.loading('Permanently deleting client...');
    try {
      await API.delete(`/admin/organization/${deleteTarget.id}/permanent`);
      toast.success(`"${deleteTarget.name}" has been permanently deleted.`, { id: toastId });
      setShowDeleteModal(false);
      setDeleteTarget(null);
      loadOrganizations();
    } catch (error) {
      toast.error('Failed to delete client', { id: toastId });
    }
    setDeleting(false);
  };

  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return (
    <div className="loading-container">
      <div className="loading-content">
        <div className="loading-spinner">⏳</div>
        <p className="loading-text">Loading clients...</p>
      </div>
    </div>
  );

  return (
    <div className="adm-page">

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 99999
        }}>
          <div style={{
            background: 'white', borderRadius: '16px',
            maxWidth: '440px', width: '90%',
            overflow: 'hidden',
            boxShadow: '0 25px 50px rgba(0,0,0,0.35)'
          }}>
            <div style={{
              background: '#ef4444', padding: '20px 24px',
              display: 'flex', alignItems: 'center', gap: '14px'
            }}>
              <div style={{
                width: '42px', height: '42px',
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                fontSize: '20px'
              }}>⚠️</div>
              <div>
                <h3 style={{ margin: 0, color: 'white', fontSize: '18px', fontWeight: '700' }}>
                  Permanently Delete Client
                </h3>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.85)', fontSize: '13px' }}>
                  This action cannot be undone
                </p>
              </div>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{ color: '#374151', fontSize: '14px', fontWeight: '600', marginBottom: '14px' }}>
                Are you sure you want to permanently delete this client?
              </p>
              <div style={{
                padding: '14px', background: '#fef2f2',
                borderRadius: '8px', borderLeft: '3px solid #ef4444',
                marginBottom: '16px'
              }}>
                <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#991b1b' }}>
                  {deleteTarget.name}
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#b91c1c' }}>
                  {deleteTarget.email}
                </p>
              </div>
              <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '20px' }}>
                All associated data including users, flows, contacts, messages, WhatsApp credentials,
                subscriptions, and payment history will be <strong>permanently removed</strong> from the system.
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                  style={{
                    flex: 1, padding: '12px',
                    background: '#f1f5f9', color: '#475569',
                    border: 'none', borderRadius: '10px',
                    fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                  }}
                >Cancel</button>
                <button
                  onClick={handleDeleteOrganization}
                  disabled={deleting}
                  style={{
                    flex: 1, padding: '12px',
                    background: deleting ? '#94a3b8' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: 'white', border: 'none',
                    borderRadius: '10px', fontSize: '14px',
                    fontWeight: '700', cursor: deleting ? 'not-allowed' : 'pointer',
                    boxShadow: '0 4px 12px rgba(239,68,68,0.4)'
                  }}
                >
                  {deleting ? '⏳ Deleting...' : '🗑️ Yes, Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="adm-header">
        <div>
          <h1 className="adm-page-title">🏢 Clients</h1>
          <p className="adm-page-subtitle">Manage all clients using the platform</p>
        </div>
      </div>

      {/* Search */}
      <div className="adm-search-wrap">
        <input
          type="text"
          placeholder="🔍 Search clients by name or email..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="adm-search-input"
        />
      </div>

      {/* Table */}
      {filteredOrgs.length > 0 ? (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead className="adm-table-head">
              <tr>
                <th className="adm-th">Client</th>
                <th className="adm-th adm-th-center">Users</th>
                <th className="adm-th adm-th-center">Flows</th>
                <th className="adm-th adm-th-center">Contacts</th>
                <th className="adm-th adm-th-center">Msgs Today</th>
                <th className="adm-th adm-th-center">WhatsApp</th>
                <th className="adm-th adm-th-center">AI Agent</th>
                <th className="adm-th adm-th-center">Status</th>
                <th className="adm-th adm-th-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrgs.map((org, index) => (
                <tr key={org.id} className={`adm-tr ${index < filteredOrgs.length - 1 ? 'adm-tr-border' : ''}`}>
                  <td onClick={() => navigate(`/admin/client/${org.id}`)} className="adm-td adm-td-click">
                    <div className="adm-client-name">{org.name}</div>
                    <div className="adm-client-email">{org.email}</div>
                    <div className="adm-client-date">Created: {new Date(org.created_at).toLocaleDateString()}</div>
                  </td>
                  <td className="adm-td adm-td-center adm-td-num">{org.user_count}</td>
                  <td className="adm-td adm-td-center adm-td-num">{org.flow_count}</td>
                  <td className="adm-td adm-td-center adm-td-num">{org.contact_count}</td>
                  <td className="adm-td adm-td-center adm-td-num">{org.messages_today || 0}</td>
                  <td className="adm-td adm-td-center">
                    {org.whatsapp_connected
                      ? <span className="adm-badge adm-badge-completed">✅ Connected</span>
                      : <span className="adm-badge adm-badge-rejected">❌ No</span>}
                  </td>
                  <td className="adm-td adm-td-center">
                    {org.ai_agent_active
                      ? <span className="adm-badge adm-badge-progress">🤖 Active</span>
                      : <span className="adm-badge adm-badge-neutral">📊 Flow</span>}
                  </td>
                  <td className="adm-td adm-td-center">
                    {org.is_active
                      ? <span className="adm-badge adm-badge-completed">✅ Active</span>
                      : <span className="adm-badge adm-badge-rejected">⏸️ Inactive</span>}
                  </td>
                  <td className="adm-td adm-td-center">
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/admin/client/${org.id}`); }}
                        className="adm-btn adm-btn-sm adm-btn-primary"
                      >👁️ View</button>
                      <button
                        onClick={e => { e.stopPropagation(); handleToggleOrganization(org.id, org.is_active, org.name); }}
                        className={`adm-btn adm-btn-sm ${org.is_active ? 'adm-btn-warning' : 'adm-btn-success'}`}
                      >{org.is_active ? '⏸️ Deactivate' : '▶️ Activate'}</button>
                      <button
                        onClick={e => { e.stopPropagation(); setDeleteTarget(org); setShowDeleteModal(true); }}
                        className="adm-btn adm-btn-sm adm-btn-danger"
                      >🗑️ Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-state-icon">🏢</div>
          <h3 className="empty-state-title">{searchTerm ? 'No clients found' : 'No clients yet'}</h3>
          <p className="empty-state-description">{searchTerm ? 'Try a different search term' : 'Clients will appear here once users sign up'}</p>
        </div>
      )}
    </div>
  );
}

export default OrganizationsList;